import { useCallback, useEffect, type FunctionComponent } from 'react';

import { ErrorBoundary } from '@trello/error-boundaries';
import type { SentryErrorMetadata } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { WorkspaceIdProvider } from '@trello/id-context';
import { RouteId, useIsActiveRoute } from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { getOrganizationUrl } from '@trello/urls';

import { LazyError } from 'app/src/components/Error/LazyError';
import { RuntimeError } from 'app/src/components/Error/RuntimeError';
import {
  PlanSelectionOverlay,
  useFreeTrialEligibilityRules,
} from 'app/src/components/FreeTrial';
import { SettingsFullPage } from 'app/src/components/SettingsFullPage';
import { WorkspaceBoardsView } from 'app/src/components/WorkspaceBoards';
import { useWorkspacePageOrganizationQuery } from './useWorkspacePageOrganizationQuery';
import { WorkspacePage } from './WorkspacePage';
import { WorkspacePageSpinner } from './WorkspacePageSpinner';

interface WorkspaceBoardsPageProps {
  orgNameOrId: string;
}

const errorBoundaryTags: SentryErrorMetadata['tags'] = {
  ownershipArea: 'trello-billing-platform',
  feature: 'Workspace Boards Page',
};

export const WorkspaceBoardsPage: FunctionComponent<
  WorkspaceBoardsPageProps
> = ({ orgNameOrId }) => {
  const {
    loading,
    organization,
    organizationId,
    isOrganizationAdmin,
    hasAccessToOrganization,
    error,
  } = useWorkspacePageOrganizationQuery({
    orgNameOrId,
  });

  const isFreeTrialRoute = useIsActiveRoute(RouteId.ORGANIZATION_FREE_TRIAL);
  const { loading: loadingEligibilityRules, isEligible } =
    useFreeTrialEligibilityRules(organization?.id, { skip: !isFreeTrialRoute });

  // If we're on the free trial route, but the workspace isn't eligible to start
  // a trial, just bounce them to the regular boards page
  useEffect(() => {
    if (
      isFreeTrialRoute &&
      !loading &&
      !loadingEligibilityRules &&
      !isEligible
    ) {
      navigate(getOrganizationUrl(orgNameOrId));
    }
  }, [
    loading,
    loadingEligibilityRules,
    isFreeTrialRoute,
    isEligible,
    orgNameOrId,
  ]);

  // Redirect to regular boards page when we close the overlay
  const onCloseOverlay = useCallback(() => {
    navigate(getOrganizationUrl(orgNameOrId));
  }, [orgNameOrId]);

  const { value: isM2WorkspaceNavEnabled } = useFeatureGate(
    'billplat_m2_workspace_pages',
  );

  if (isM2WorkspaceNavEnabled) {
    return (
      <ErrorBoundary
        tags={errorBoundaryTags}
        errorHandlerComponent={RuntimeError}
      >
        <WorkspaceIdProvider value={organizationId}>
          <SettingsFullPage>
            {loading ? (
              <WorkspacePageSpinner />
            ) : error || !organization ? (
              <LazyError errorType={error ? 'serverError' : 'notFound'} />
            ) : (
              <>
                <WorkspaceBoardsView />
                {isFreeTrialRoute && isEligible && (
                  <PlanSelectionOverlay
                    onClose={onCloseOverlay}
                    orgId={organization.id}
                  />
                )}
              </>
            )}
          </SettingsFullPage>
        </WorkspaceIdProvider>
      </ErrorBoundary>
    );
  }

  if (loading) {
    return <WorkspacePageSpinner />;
  }

  if (error || !organization || !hasAccessToOrganization) {
    return (
      <LazyError
        errorType={
          !error || !hasAccessToOrganization ? 'notFound' : 'serverError'
        }
      />
    );
  }

  return (
    <ErrorBoundary
      tags={errorBoundaryTags}
      errorHandlerComponent={RuntimeError}
    >
      <WorkspaceIdProvider value={organization.id}>
        <WorkspacePage
          orgId={organization.id}
          isOrgAdmin={isOrganizationAdmin}
          fullWidth
        >
          <WorkspaceBoardsView />
        </WorkspacePage>
        {isFreeTrialRoute && isEligible && (
          <PlanSelectionOverlay
            onClose={onCloseOverlay}
            orgId={organization.id}
          />
        )}
      </WorkspaceIdProvider>
    </ErrorBoundary>
  );
};
